// One group per connectable Google surface, as the user understands them.
//
// `userinfo.email` belongs to no group on purpose: getOAuthUrl unions it into every
// request, because it is what resolves the account address that keys the credential row.
//
// Contacts is deliberately NOT its own group either — it rides inside GMAIL. The mailbox
// features depend on the contacts scopes to resolve senders and recipients, so a Gmail
// connection without them is a broken Gmail connection, not a leaner one.
export const GOOGLE_SCOPE_GROUPS = {
  GMAIL: 'gmail',
  CALENDAR: 'calendar',
  CHAT: 'chat',
  DRIVE: 'drive',
  BUSINESS_PROFILE: 'business_profile',
} as const;

export type GoogleScopeGroup =
  (typeof GOOGLE_SCOPE_GROUPS)[keyof typeof GOOGLE_SCOPE_GROUPS];

export interface GoogleKeys {
  userId: string; // Primary key
  // The Google ACCOUNT email (from userinfo.email), not necessarily a Gmail mailbox — a
  // Calendar-only or Drive-only connect fills it too. Name is a misnomer and cannot be
  // fixed: it is the DynamoDB sort key.
  gmailAddress: string; // Sort key
}

export interface GoogleFields {
  refreshToken: string;
  gmailSchedulerName?: string;
  // Where the full-sync backfill stopped when it hit MAX_ITERATIONS. The dead-lettered
  // message ages out, so without this the resume point exists only in a log line.
  gmailBackfillTruncatedPageToken?: string;
  gmailBackfillTruncatedAt?: number;
  gmailHistoryId?: string;
  gmailHistoryIdUpdatedAt?: number;
  gmailExpiration?: string;
  chatSubscriptionId?: string;
  shouldPreserveData?: boolean;
  /**
   * The surface the user asked to remove, stamped just before the row is deleted so the
   * REMOVE stream can scope the deletion to it.
   *
   * The revoke is all-or-nothing, so disconnecting Gmail takes Chat down with it — but the
   * user never asked to remove Chat, so its messages must survive. Same reasoning as
   * `tearDownSharedGrantHolders`. ABSENT means account deletion, where everything goes —
   * which is why the erasure path CLEARS this rather than relying on never having set it,
   * and why it needs `isAccountErasure` below to stay distinguishable afterwards.
   */
  disconnectedScopeGroup?: GoogleScopeGroup;
  /**
   * Stamped just before an ACCOUNT DELETION removes this row, so the REMOVE stream can tell
   * an erasure from a disconnect.
   *
   * Absence of the other two flags cannot carry that: the erasure path clears them both on
   * purpose (a leftover scope group or `shouldPreserveData` would narrow the deletion), so
   * "nothing stamped" is exactly what an erasure looks like AND what a plain teardown looks
   * like. A positive marker is the only thing that separates them. Consumed by
   * `processRemove` to stay silent — a user mid-erasure must not be handed a notification
   * row written after their notifications were already swept.
   *
   * It covers `removeUserGoogleIntegrations`, the path DeleteUserData actually takes. The
   * registry backstop sweep in `userScopedTables.ts` and `apps/scripts/findOrphanedUserRows.ts`
   * are raw BatchWriteItem, so nothing stamps them — those are covered instead by the
   * `userExists` check in `notifyRevokeFailed`, which works there precisely because the user
   * really is gone by then, and does NOT work here because DeleteUserData removes the USERS
   * row last. The two guards are complements; neither covers both.
   */
  isAccountErasure?: boolean;
  /**
   * Stamped once, by the first connect that created this row.
   *
   * The table records plenty of activity timestamps and not one creation time, so there was
   * no way to tell which of two rows on the same Google account got there first — see
   * `resolveAccountOwner`. Absent on every row written before this shipped, which is exactly
   * what makes those rows sort as the oldest without a backfill.
   */
  connectedAt?: number;
  grantedScopes?: string[];
  // Google Business Profile resource names granted (`accounts/{id}`).
  gbpAccountNames?: string[];
  // Selected location resource names; empty/undefined = all locations under the granted accounts.
  gbpLocationNames?: string[];
  gbpNotificationsConfiguredAt?: number;
  // Set once the connect-time reviews backfill completes for this row (all granted accounts).
  gbpReviewsSyncedAt?: number;
}

export interface GoogleItem extends GoogleKeys, GoogleFields {}

/**
 * Why a connect was refused, read off the response body by the web app.
 *
 * A value rather than prose because every caller that schedules a connect renders one
 * generic toast on any rejection — a thrown sentence reaches the user as "something went
 * wrong" and nothing else.
 */
export enum GoogleConnectRefusal {
  /**
   * Another app user already holds this Google account.
   *
   * The grant is left alone on purpose. It was consented before this code could know which
   * account it was for — the address comes from `getBasicProfile`, which needs the token —
   * and Google issues one grant per (account, client_id), so revoking it here would kill
   * the holder's integration rather than the requester's.
   */
  ACCOUNT_HELD_BY_ANOTHER_USER = 'account_held_by_another_user',
}

/**
 * What became of the Google OAuth grant when the account was disconnected.
 *
 * Google issues one grant per (account, client_id), so a disconnect cannot always revoke:
 * another user of this app may hold the same Google account. The three outcomes are
 * genuinely different things to tell a person, and collapsing them is the exact "the UI
 * says one thing and Google did another" claim this whole sequence exists to stop making.
 */
export enum GoogleRevokeOutcome {
  /** The grant is gone, and this user was the only one on it. */
  REVOKED = 'revoked',
  /**
   * The grant is gone, and it took other users of this app with it.
   *
   * Google issues one grant per (account, client_id), so there is no partial revoke: the
   * moment two people connect the same Google account they are on the same grant, and
   * disconnecting is all-or-nothing for both. The disconnect still runs — leaving a live
   * grant behind would be the fictitious disconnect this whole sequence removes — but the
   * user is told, because someone else's integration just stopped working.
   */
  REVOKED_SHARED = 'revoked_shared',
  /**
   * The revoke could not be attempted or did not succeed, and cannot be retried — the
   * refresh token lived on the row that is now deleted. Only the user can finish it, at
   * myaccount.google.com.
   *
   * It deliberately does NOT distinguish "the grant was already gone" from "the grant may
   * still be live", because that distinction is not available to us. An `invalid_grant` has
   * seven documented causes and only one of them is the user revoking the app — a password
   * change with Gmail scopes, six months idle, or too many live refresh tokens all kill the
   * token while the app stays listed at myaccount.google.com. An attempt to split this
   * outcome on that error silenced the banner in exactly the case that needs it. What the
   * copy says is therefore "we could not confirm", which is the true statement.
   */
  FAILED_UNRECOVERABLE = 'failed_unrecoverable',
}

/**
 * What a disconnected account had connected, kept so one OAuth round trip can restore it.
 *
 * Written before the credential row is deleted, because `grantedScopes` and
 * `gbpLocationNames` live only on that row. The location list is the reason this is not
 * just a scope list: a NARROWED selection is what a restore can lose. An empty one is that
 * field's documented default ("every location under the granted accounts"), so it needs no
 * carrying across — see `applyGoogleRestoreSnapshotFields` for the gates on putting one back.
 *
 * `gmailAddress` is the plain account address, as on every other row of this table. It is a
 * SORT KEY, so it lands on the `gmailAddress` GSI beside the credential rows — which is why
 * `getAllGoogleItemsByGmailAddress` drops synthetic-PK rows: unfiltered, a snapshot would
 * read as another holder of the grant and suppress the revoke.
 */
export interface GoogleRestoreSnapshot extends GoogleKeys {
  scopeGroups: GoogleScopeGroup[];
  gbpLocationNames?: string[];
  capturedAt: number;
  /** Epoch SECONDS — DynamoDB TTL, not milliseconds. */
  expiresAt: number;
  isGoogleRestoreSnapshot: true;
}

export interface GoogleIntegrationStatus extends GoogleKeys {
  gmailEnabled: boolean;
  calendarEnabled: boolean;
  chatEnabled: boolean;
  driveEnabled: boolean;
  businessProfileEnabled: boolean;
  // Mirrors GoogleFields.gbpLocationNames; null/undefined/empty = all locations.
  gbpSelectedLocationNames?: string[];
}
